/**
 * jsonText — the "จัดรูปแบบ JSON" button's whole brain, kept OUT of the SFC so it can be unit-tested
 * under plain Node (see tests/jsonText.test.mjs). `TaxQuestionItemV2Dialog.vue` imports it.
 *
 * The boxes it serves are the raw JSON editors of a Tax Question V2 item — a SUMMARY card's config and
 * template, a FILING_CHOICE / SIMULATE config, and a SHORT_FORM's config, template and style bags. Every
 * one of them is a document an admin hand-authors and the backend stores VERBATIM, which is what makes a
 * formatter both useful and dangerous: it is the one control in this dialog that rewrites the author's
 * text rather than adding to it.
 *
 * 🔴 RULE 1 — IT CAN ONLY EVER FORMAT A DOCUMENT THAT REALLY PARSES. Parse first, print second: the button
 * can therefore never turn a valid config into an invalid one, and it can never "half-format" a document.
 * A regex or a hand-rolled indenter over the raw text could do both.
 *
 * 🔴 RULE 2 — ANYTHING IT CANNOT FORMAT, IT LEAVES ALONE. Mid-edit is the normal state of these boxes, and
 * a formatter that reacted to a half-typed document by rewriting or clearing it would be a button nobody
 * dares press. `null` is the answer, and every caller reads it as "do not touch the box".
 *
 * Pure logic (NO Vue / framework import) so it runs in plain Node.
 */

/** How deep one level of nesting is indented. Two spaces: these documents nest 5–6 levels and four would
 *  push a card template off the right-hand edge of the textarea it is read in. */
const INDENT = 2;

/**
 * `raw` re-printed with stable indentation, or `null` when it is not JSON.
 *
 * 🔴 What it normalises is exactly what JSON itself calls equal — `1.0` prints as `1`, escapes are
 * canonicalised, and a document carrying the same key twice keeps the last one (which is the only one any
 * parser was ever going to read). Key ORDER is preserved, because an author reading the diff afterwards
 * has to recognise their own file.
 *
 * @param {*} raw The textarea's current contents.
 * @returns {?string} The formatted document, or null when `raw` is blank or does not parse.
 */
export function formatJsonText(raw) {
  const text = String(raw == null ? '' : raw).trim();
  if (!text) return null;
  try {
    return JSON.stringify(JSON.parse(text), null, INDENT);
  } catch {
    return null;
  }
}

/**
 * Is there something here the button could format? Drives its `:disabled`, so a box holding a half-typed
 * document shows a greyed-out button rather than one that looks alive and silently does nothing.
 *
 * @param {*} raw
 * @returns {boolean}
 */
export function canFormatJsonText(raw) {
  return formatJsonText(raw) !== null;
}
